import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import { promiseWrapper } from '@/helpers';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import {
  PaymentMethod,
  PaymentMethods,
  paymentsServices,
  PlatformFee,
  StoredPaymentMethod,
} from '../payments.services';

export type UsePaymentMethodsContextType = ReturnType<typeof usePaymentMethodsManager> & {
  isBokadirektPay: boolean;
};

export const usePaymentMethodsManager = (
  placeId: number | null,
  employeeId: number | null,
  fetch: boolean = true,
  organisationNumber: string | null = null,
): {
  paymentMethods: PaymentMethod[];
  storedPaymentMethods: StoredPaymentMethod[];
  platformFee: PlatformFee | null;
  dealsEnabled: boolean;
  isBokadirektPayMerchant: boolean;
  loading: boolean;
  loadPaymentMethods: () => void;
  error: boolean;
  manualSetPaymentMethods: (methods: PaymentMethods) => void;
} => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [storedPaymentMethods, setStoredPaymentMethods] = useState<StoredPaymentMethod[]>([]);
  const [platformFee, setPlatformFee] = useState<PlatformFee | null>(null);
  const [dealsEnabled, setDealsEnabled] = useState(false);
  const [isBokadirektPayMerchant, setIsBokadirektPayMerchant] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(fetch);
  const [fetchPaymentMethods, setFetchPaymentMethods] = useState(fetch);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setFetchPaymentMethods(fetch);
  }, [fetch]);

  useEffect(() => {
    const hasOrgKey = !!organisationNumber;
    const hasPlaceKey = !!placeId && !!employeeId;
    if (fetchPaymentMethods && (hasOrgKey || hasPlaceKey)) {
      setFetchPaymentMethods(false);
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      (async () => {
        setLoading(true);
        setError(false);
        const request = hasOrgKey
          ? paymentsServices.getMerchantSettingsByOrg(organisationNumber!, placeId, signal)
          : paymentsServices.getMerchantSettings(placeId!, employeeId!, signal);
        const { data, error } = await promiseWrapper(request);

        if (signal.aborted) return;
        if (error) {
          setError(true);
        } else {
          setPaymentMethods(data.paymentMethods.paymentMethods);
          setStoredPaymentMethods(data.paymentMethods.storedPaymentMethods);
          setPlatformFee(data.platformFee);
          setDealsEnabled(data.dealsEnabled);
          setIsBokadirektPayMerchant(data.isBokadirektPayMerchant);
        }
        setLoading(false);
      })();
    }
  }, [fetchPaymentMethods, placeId, employeeId, organisationNumber]);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const loadPaymentMethods = () => setFetchPaymentMethods(true);

  const manualSetPaymentMethods = (methods: PaymentMethods) => {
    setPaymentMethods(methods.paymentMethods);
    setStoredPaymentMethods(methods.storedPaymentMethods);
  };

  return {
    paymentMethods,
    storedPaymentMethods,
    platformFee,
    dealsEnabled,
    isBokadirektPayMerchant,
    error,
    loading,
    loadPaymentMethods,
    manualSetPaymentMethods,
  };
};

const UsePaymentMethodsContext = createContext<UsePaymentMethodsContextType | null>(null);

export const usePaymentMethodsContext = (): UsePaymentMethodsContextType => {
  const context = useContext(UsePaymentMethodsContext);
  if (!context) {
    throw new Error('usePaymentMethodsContext must be used within a UsePaymentMethodsProvider');
  }
  return context;
};

export const usePaymentMethodsContextSafe = (): UsePaymentMethodsContextType | null => {
  return useContext(UsePaymentMethodsContext);
};

export const UsePaymentMethodsProvider: React.FC<{
  children: ReactNode;
  placeId: number | null;
  employeeId: number | null;
  organisationNumber?: string | null;
  fetchOnMount?: boolean;
  isBokadirektPay?: boolean;
}> = ({ children, placeId, employeeId, organisationNumber = null, fetchOnMount = false, isBokadirektPay }) => {
  const manager = usePaymentMethodsManager(placeId, employeeId, fetchOnMount, organisationNumber);

  const derivedIsBokadirektPay = isBokadirektPay !== undefined ? isBokadirektPay : manager.isBokadirektPayMerchant;

  const value: UsePaymentMethodsContextType = { ...manager, isBokadirektPay: derivedIsBokadirektPay };
  return <UsePaymentMethodsContext.Provider value={value}>{children}</UsePaymentMethodsContext.Provider>;
};

export const WithPaymentMethodsLoaded: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading } = usePaymentMethodsContext();
  if (loading) return <LoadingPlaceHolder />;
  return <>{children}</>;
};
